import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import {
  ALL_ADJUSTMENT_KINDS,
  type AdjustmentView,
  type AllocationView,
  type InvoiceLineView,
} from '@lims/shared-types';
import { invoicesApi, paymentsApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  ConfirmDialog,
  DataTable,
  Dialog,
  ErrorState,
  LoadingState,
  NumberField,
  Panel,
  ReadOnlyField,
  SelectField,
  StatusBadge,
  TextAreaField,
  useToast,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDate, formatMoney } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { displayName } from '../clients/bilingual.js';
import {
  ContextItem,
  Facts,
  RecordChain,
  RecordGrid,
  RecordHeader,
  RecordLink,
  RecordNumber,
  Totals,
} from '../clients/record-layout.js';
import { FinanceMutationError } from './finance-errors.js';
import {
  InvoiceLineEditor,
  lineFromExisting,
  linePayload,
  type LineDraft,
} from './InvoiceLineEditor.js';

type DialogKind = 'issue' | 'discard' | 'cancel' | 'adjust' | null;

/**
 * The invoice: a financial record, not a tax invoice (D160).
 *
 * ONE PAGE, TWO SHAPES. A draft is edited in place here — its own lines, its
 * own document discount, its own notes — because Phase 11 gives it no
 * separate edit route (contract section 8). Once issued, the same page turns
 * read-only and grows the acts an issued document can still take: an
 * adjustment, a cancellation, a payment. Nothing here recomputes a total or a
 * balance; every figure is exactly what the server returned.
 */
export function InvoiceDetailPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { show } = useToast();
  const locale = currentLocale();

  const canOverride = can(PERMISSIONS.PRICING_OVERRIDE);

  const [editing, setEditing] = useState(false);
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [pickedServiceId, setPickedServiceId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [reason, setReason] = useState('');
  const [adjustmentKind, setAdjustmentKind] = useState<string | null>('credit');
  const [adjustmentAmount, setAdjustmentAmount] = useState<number | null>(null);
  const [reversing, setReversing] = useState<AllocationView | null>(null);
  const [reversalReason, setReversalReason] = useState('');

  const invoice = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesApi.get(id),
    enabled: Boolean(id),
  });

  const billable = useQuery({
    queryKey: ['billable-services', 'for-client', invoice.data?.client.id],
    queryFn: () =>
      invoicesApi.billableServices({
        clientId: invoice.data?.client.id as string,
        pageSize: 200,
      }),
    enabled: editing && Boolean(invoice.data?.client.id),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['invoice', id] });
    await queryClient.invalidateQueries({ queryKey: ['invoices'] });
  };

  const startEditing = () => {
    if (!invoice.data) return;
    setLines(invoice.data.lines.map(lineFromExisting));
    setEditing(true);
  };

  const saveDraft = useMutation({
    mutationFn: () =>
      invoicesApi.update(id, {
        draftRevision: invoice.data?.draftRevision,
        lines: linePayload(lines),
      }),
    onSuccess: async () => {
      await invalidate();
      setEditing(false);
      show({ tone: 'success', message: t('invoices.draftSaved') });
    },
  });

  const issue = useMutation({
    mutationFn: () =>
      invoicesApi.issue(id, { draftRevision: invoice.data?.draftRevision as number }),
    onSuccess: async () => {
      await invalidate();
      setDialog(null);
      show({ tone: 'success', message: t('invoices.issued') });
    },
  });

  const discard = useMutation({
    mutationFn: () => invoicesApi.discard(id, { reason: reason.trim() }),
    onSuccess: async () => {
      await invalidate();
      setDialog(null);
      setReason('');
      show({ tone: 'success', message: t('invoices.discarded') });
    },
  });

  const cancelInvoice = useMutation({
    mutationFn: () => invoicesApi.cancel(id, { reason: reason.trim() }),
    onSuccess: async () => {
      await invalidate();
      setDialog(null);
      setReason('');
      show({ tone: 'success', message: t('invoices.cancelled') });
    },
  });

  const recordAdjustment = useMutation({
    mutationFn: () =>
      invoicesApi.recordAdjustment(id, {
        kind: adjustmentKind as string,
        amountMinor: Math.round((adjustmentAmount ?? 0) * 100),
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      await invalidate();
      setDialog(null);
      setReason('');
      setAdjustmentAmount(null);
      show({ tone: 'success', message: t('invoices.adjustmentRecorded') });
    },
  });

  const reverseAllocation = useMutation({
    mutationFn: () =>
      paymentsApi.reverseAllocation(
        (reversing as AllocationView).paymentId,
        (reversing as AllocationView).id,
        { reason: reversalReason.trim() },
      ),
    onSuccess: async () => {
      await invalidate();
      setReversing(null);
      setReversalReason('');
      show({ tone: 'success', message: t('invoices.allocationReversed') });
    },
  });

  if (invoice.isLoading) return <LoadingState label={t('common.loading')} />;
  if (invoice.error) {
    return (
      <ErrorState error={invoice.error} onRetry={() => void invoice.refetch()} />
    );
  }
  const record = invoice.data;
  if (!record) return null;

  const currency = record.currency;
  const money = (minor: number) => formatMoney(minor, { locale, currency });

  const usedServiceIds = new Set(
    lines.map((line) => line.workOrderServiceId).filter(Boolean),
  );
  const availableServices = (billable.data?.data ?? []).filter(
    (service) => !usedServiceIds.has(service.id),
  );

  const lineColumns: readonly DataColumn<InvoiceLineView>[] = [
    {
      key: 'description',
      header: t('billing.description'),
      render: (line) => (
        <div className="min-w-0">
          <span className="block text-default-900">
            {displayName(line.description, locale) ?? t('common.notRecorded')}
          </span>
          {line.workOrderService ? (
            <span className="text-caption">
              <RecordNumber to={`/work-orders/${line.workOrderService.workOrderId}`}>
                {line.workOrderService.workOrderNumber}
              </RecordNumber>
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'quantity',
      header: t('invoices.quantity'),
      align: 'end',
      render: (line) => (
        <span dir="ltr" className="measure">
          {line.quantity}
        </span>
      ),
    },
    {
      key: 'unitPrice',
      header: t('invoices.unitPrice'),
      align: 'end',
      render: (line) => (
        <div className="text-end">
          <span className="measure block text-default-900" dir="ltr">
            {money(line.unitPriceMinor)}
          </span>
          <span className="text-caption text-default-600">
            {t(`invoicePriceSource.${line.priceSource}`)}
          </span>
        </div>
      ),
    },
    {
      key: 'total',
      header: t('invoices.total'),
      align: 'end',
      render: (line) => (
        <span className="measure text-default-900" dir="ltr">
          {money(line.totalMinor)}
        </span>
      ),
    },
  ];

  const allocationColumns: readonly DataColumn<AllocationView>[] = [
    {
      key: 'payment',
      header: t('payments.title'),
      render: (allocation) => (
        <ActionButton
          priority="tertiary"
          size="sm"
          onPress={() => navigate(`/payments/${allocation.paymentId}`)}
        >
          {t('common.open')}
        </ActionButton>
      ),
    },
    {
      key: 'amount',
      header: t('invoices.amount'),
      align: 'end',
      render: (allocation) => (
        <span dir="ltr" className="measure">
          {money(allocation.amountMinor)}
        </span>
      ),
    },
    {
      key: 'when',
      header: t('common.recordedAt'),
      render: (allocation) => (
        <span className="text-default-600">
          <span className="whitespace-nowrap">{formatDate(allocation.allocatedAt, { locale })}</span>
          {allocation.allocatedBy.displayName
            ? ` — ${allocation.allocatedBy.displayName}`
            : ''}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (allocation) =>
        allocation.reversal ? (
          <StatusBadge label={t('invoices.reversed')} tone="warning" />
        ) : (
          <StatusBadge label={t('common.active')} tone="success" />
        ),
    },
    {
      key: 'reversal',
      header: t('common.actions'),
      align: 'end',
      render: (allocation) =>
        !allocation.reversal && can(PERMISSIONS.PAYMENTS_ADJUST) ? (
          <ActionButton
            priority="tertiary"
            size="sm"
            onPress={() => setReversing(allocation)}
          >
            {t('invoices.reverseAllocation')}
          </ActionButton>
        ) : null,
    },
  ];

  const adjustmentColumns: readonly DataColumn<AdjustmentView>[] = [
    {
      key: 'kind',
      header: t('invoices.adjustmentKindLabel'),
      render: (adjustment) => (
        <StatusBadge
          label={t(`adjustmentKind.${adjustment.kind}`)}
          tone={adjustment.kind === 'credit' ? 'success' : 'warning'}
        />
      ),
    },
    {
      key: 'amount',
      header: t('invoices.amount'),
      align: 'end',
      render: (adjustment) => (
        <span dir="ltr" className="measure">
          {money(adjustment.amountMinor)}
        </span>
      ),
    },
    {
      key: 'effectiveOn',
      header: t('invoices.effectiveOn'),
      render: (adjustment) => formatDate(adjustment.effectiveOn, { locale }),
    },
    {
      key: 'reason',
      header: t('common.reason'),
      render: (adjustment) => adjustment.reason,
    },
  ];

  const notRecorded = t('common.notRecorded');
  const clientLabel = record.client.name ?? record.client.number;
  // The work orders this invoice bills, once each, as its lines name them.
  const workOrders = [
    ...new Map(
      record.lines
        .filter((line) => line.workOrderService)
        .map((line) => [
          line.workOrderService?.workOrderId ?? '',
          line.workOrderService?.workOrderNumber ?? '',
        ]),
    ),
  ];

  return (
    <>
      <RecordHeader
        {...(record.invoiceNumber ? { number: record.invoiceNumber } : {})}
        badges={
          <>
            <StatusBadge
              label={t(`status.invoice.${record.status}`)}
              tone={
                record.status === 'issued'
                  ? 'success'
                  : record.status === 'draft'
                    ? 'neutral'
                    : 'warning'
              }
            />
            {/* The DERIVED payment state: a separate fact from the status. */}
            {record.paymentStatus ? (
              <StatusBadge
                label={t(`status.payment.${record.paymentStatus}`)}
                tone={
                  record.paymentStatus === 'paid'
                    ? 'success'
                    : record.paymentStatus === 'partially_paid'
                      ? 'warning'
                      : 'neutral'
                }
              />
            ) : null}
            {record.overdue ? (
              <StatusBadge label={t('invoices.overdue')} tone="warning" />
            ) : null}
          </>
        }
        title={clientLabel}
        context={
          <>
            <ContextItem label={t('invoices.client')}>
              <RecordLink to={`/clients/${record.client.id}`}>{clientLabel}</RecordLink>
            </ContextItem>
            <ContextItem label={t('invoices.dueDate')}>
              {record.dueDate ? formatDate(record.dueDate, { locale }) : notRecorded}
            </ContextItem>
          </>
        }
        breadcrumbs={[
          { label: t('nav.invoices'), to: '/invoices' },
          { label: record.invoiceNumber ?? t('status.invoice.draft') },
        ]}
        actions={
          <ActionBar>
            {record.can.edit && !editing && can(PERMISSIONS.INVOICES_CREATE) ? (
              <ActionButton priority="secondary" onPress={startEditing}>
                {t('common.edit')}
              </ActionButton>
            ) : null}
            {record.can.issue && !editing && can(PERMISSIONS.INVOICES_ISSUE) ? (
              <ActionButton priority="primary" onPress={() => setDialog('issue')}>
                {t('invoices.issue')}
              </ActionButton>
            ) : null}
            {record.can.discard && !editing && can(PERMISSIONS.INVOICES_CREATE) ? (
              <ActionButton
                priority="destructive"
                onPress={() => setDialog('discard')}
              >
                {t('invoices.discard')}
              </ActionButton>
            ) : null}
            {record.can.cancel && can(PERMISSIONS.INVOICES_CANCEL) ? (
              <ActionButton
                priority="destructive"
                onPress={() => setDialog('cancel')}
              >
                {t('invoices.cancel')}
              </ActionButton>
            ) : null}
            {record.can.adjust && can(PERMISSIONS.PAYMENTS_ADJUST) ? (
              <ActionButton
                priority="secondary"
                onPress={() => setDialog('adjust')}
              >
                {t('invoices.recordAdjustment')}
              </ActionButton>
            ) : null}
            {record.can.recordPayment && can(PERMISSIONS.PAYMENTS_RECORD) ? (
              <ActionButton
                priority="primary"
                onPress={() =>
                  navigate(
                    `/payments/new?clientId=${record.client.id}&invoiceId=${record.id}`,
                  )
                }
              >
                {t('invoices.recordPayment')}
              </ActionButton>
            ) : null}
          </ActionBar>
        }
      />

      <div className="mb-stack">
        <Alert tone="info" title={t('invoices.notTaxInvoiceNotice')} />
      </div>

      <div className="mb-stack">
        <FinanceMutationError
          error={
            saveDraft.error ??
            issue.error ??
            discard.error ??
            cancelInvoice.error ??
            recordAdjustment.error ??
            reverseAllocation.error
          }
        />
      </div>

      {record.cancellation ? (
        <div className="mb-stack">
          <Alert
            tone="warning"
            title={t('invoices.cancelledNotice')}
            description={`${record.cancellation.reason} — ${formatDate(record.cancellation.at, { locale })}`}
          />
        </div>
      ) : null}
      {record.discard ? (
        <div className="mb-stack">
          <Alert
            tone="warning"
            title={t('invoices.discardedNotice')}
            description={`${record.discard.reason} — ${formatDate(record.discard.at, { locale })}`}
          />
        </div>
      ) : null}

      <RecordGrid
        side={
          <>
            {/* What is owed, what has settled it and what remains — the
                server's figures, never recomputed here. */}
            <Panel title={t('invoices.balance')}>
              <Facts
                items={[
                  {
                    label: t('invoices.amountDue'),
                    value: (
                      <span dir="ltr" className="measure">
                        {money(record.amountDueMinor)}
                      </span>
                    ),
                  },
                  {
                    label: t('invoices.settled'),
                    value: (
                      <span dir="ltr" className="measure">
                        {money(record.settledMinor)}
                      </span>
                    ),
                  },
                  {
                    label: t('invoices.balance'),
                    value: (
                      <span dir="ltr" className="measure font-semibold">
                        {money(record.balanceMinor)}
                      </span>
                    ),
                  },
                ]}
              />
            </Panel>

            <Panel title={t('common.details')}>
              <Facts
                items={[
                  {
                    label: t('invoices.issueDate'),
                    value: record.issueDate
                      ? formatDate(record.issueDate, { locale })
                      : notRecorded,
                  },
                  {
                    label: t('invoices.dueDate'),
                    value: record.dueDate
                      ? formatDate(record.dueDate, { locale })
                      : notRecorded,
                  },
                  {
                    label: t('invoices.preparedBy'),
                    value: record.preparedBy.displayName ?? notRecorded,
                  },
                  record.issuedBy
                    ? {
                        label: t('invoices.issuedBy'),
                        value: record.issuedBy.displayName ?? notRecorded,
                      }
                    : null,
                ]}
              />
            </Panel>

            <Panel title={t('ordersRecord.chain')}>
              <RecordChain
                items={[
                  {
                    label: t('invoices.client'),
                    value: (
                      <RecordLink to={`/clients/${record.client.id}`} isNumber>
                        {record.client.number}
                      </RecordLink>
                    ),
                  },
                  ...workOrders.map(([workOrderId, workOrderNumber]) => ({
                    label: t('workOrders.number'),
                    value: (
                      <RecordLink to={`/work-orders/${workOrderId}`} isNumber>
                        {workOrderNumber}
                      </RecordLink>
                    ),
                  })),
                ]}
              />
            </Panel>
          </>
        }
      >
        <Panel
          title={t('invoices.lines')}
          actions={
            editing ? (
              <ActionBar>
                <ActionButton
                  priority="tertiary"
                  size="sm"
                  onPress={() => setEditing(false)}
                >
                  {t('common.cancel')}
                </ActionButton>
                <ActionButton
                  priority="primary"
                  size="sm"
                  isLoading={saveDraft.isPending}
                  onPress={() => saveDraft.mutate()}
                >
                  {t('common.save')}
                </ActionButton>
              </ActionBar>
            ) : null
          }
        >
          {editing ? (
            <InvoiceLineEditor
              lines={lines}
              setLines={setLines}
              availableServices={availableServices}
              pickedServiceId={pickedServiceId}
              setPickedServiceId={setPickedServiceId}
              currency={currency}
              canOverride={canOverride}
              locale={locale}
            />
          ) : (
            <>
              <DataTable
                label={t('invoices.lines')}
                columns={lineColumns}
                rows={record.lines}
                rowKey={(line) => line.id}
                emptyContent={t('invoices.noLinesYet')}
              />
              <Totals
                rows={[
                  { label: t('invoices.subtotal'), value: money(record.totals.subtotalMinor) },
                  {
                    label: t('invoices.lineDiscountTotal'),
                    value: money(record.totals.lineDiscountMinor),
                  },
                  {
                    label: t('invoices.documentDiscountTotal'),
                    value: money(record.totals.documentDiscountMinor),
                  },
                  { label: t('invoices.taxable'), value: money(record.totals.taxableMinor) },
                  {
                    label: t('invoices.tax'),
                    value: (
                      <>
                        {money(record.totals.taxMinor)} ({record.taxRatePercent}%)
                      </>
                    ),
                  },
                ]}
                total={{
                  label: t('invoices.grandTotal'),
                  value: money(record.totals.totalMinor),
                }}
              />
            </>
          )}
        </Panel>

        {record.allocations.length > 0 ? (
          <Panel title={t('invoices.allocations')}>
            <DataTable
              label={t('invoices.allocations')}
              columns={allocationColumns}
              rows={record.allocations}
              rowKey={(allocation) => allocation.id}
              emptyContent={t('invoices.noAllocations')}
            />
          </Panel>
        ) : null}

        {record.adjustments.length > 0 ? (
          <Panel title={t('invoices.adjustments')}>
            <DataTable
              label={t('invoices.adjustments')}
              columns={adjustmentColumns}
              rows={record.adjustments}
              rowKey={(adjustment) => adjustment.id}
              emptyContent={t('invoices.noAdjustments')}
            />
          </Panel>
        ) : null}

        {record.clientNote || record.internalNote ? (
          <Panel title={t('invoices.notes')}>
            <div className="grid gap-stack sm:grid-cols-2">
              {record.clientNote ? (
                <ReadOnlyField label={t('invoices.clientNote')}>
                  <span className="whitespace-pre-line" dir="auto">
                    {record.clientNote}
                  </span>
                </ReadOnlyField>
              ) : null}
              {record.internalNote ? (
                <ReadOnlyField label={t('invoices.internalNote')}>
                  <span className="whitespace-pre-line" dir="auto">
                    {record.internalNote}
                  </span>
                </ReadOnlyField>
              ) : null}
            </div>
          </Panel>
        ) : null}
      </RecordGrid>

      <ConfirmDialog
        isOpen={dialog === 'issue'}
        onClose={() => setDialog(null)}
        title={t('invoices.issueTitle')}
        message={t('invoices.issueMessage')}
        confirmLabel={t('invoices.issue')}
        isPending={issue.isPending}
        onConfirm={() => issue.mutate()}
      />

      <ConfirmDialog
        isOpen={dialog === 'discard'}
        onClose={() => setDialog(null)}
        title={t('invoices.discardTitle')}
        message={t('invoices.discardMessage')}
        confirmLabel={t('invoices.discard')}
        confirmPriority="destructive"
        isPending={discard.isPending}
        onConfirm={() => discard.mutate()}
      >
        <TextAreaField
          label={t('common.reason')}
          value={reason}
          onChange={setReason}
          isRequired
          rows={3}
        />
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={dialog === 'cancel'}
        onClose={() => setDialog(null)}
        title={t('invoices.cancelTitle')}
        message={t('invoices.cancelMessage')}
        confirmLabel={t('invoices.cancel')}
        confirmPriority="destructive"
        isPending={cancelInvoice.isPending}
        onConfirm={() => cancelInvoice.mutate()}
      >
        <TextAreaField
          label={t('common.reason')}
          value={reason}
          onChange={setReason}
          isRequired
          rows={3}
        />
      </ConfirmDialog>

      <Dialog
        isOpen={dialog === 'adjust'}
        onClose={() => setDialog(null)}
        title={t('invoices.recordAdjustment')}
        footer={
          <ActionBar>
            <ActionButton priority="tertiary" onPress={() => setDialog(null)}>
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isDisabled={!adjustmentAmount || !reason.trim()}
              isLoading={recordAdjustment.isPending}
              onPress={() => recordAdjustment.mutate()}
            >
              {t('common.save')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="grid gap-stack">
          <SelectField
            label={t('invoices.adjustmentKindLabel')}
            value={adjustmentKind}
            onChange={setAdjustmentKind}
            options={ALL_ADJUSTMENT_KINDS.map((value) => ({
              value,
              label: t(`adjustmentKind.${value}`),
            }))}
          />
          <NumberField
            label={t('invoices.amount')}
            value={adjustmentAmount}
            onChange={setAdjustmentAmount}
            step="0.01"
            unit={currency}
          />
          <TextAreaField
            label={t('common.reason')}
            value={reason}
            onChange={setReason}
            isRequired
            rows={2}
          />
        </div>
      </Dialog>

      <ConfirmDialog
        isOpen={reversing !== null}
        onClose={() => setReversing(null)}
        title={t('invoices.reverseAllocationTitle')}
        message={t('invoices.reverseAllocationMessage')}
        confirmLabel={t('invoices.reverseAllocation')}
        confirmPriority="destructive"
        isPending={reverseAllocation.isPending}
        onConfirm={() => reverseAllocation.mutate()}
      >
        <TextAreaField
          label={t('common.reason')}
          value={reversalReason}
          onChange={setReversalReason}
          isRequired
          rows={2}
        />
      </ConfirmDialog>
    </>
  );
}
